// Page-wide plumbing: api() (the only way to call the server, or the browser's store for a webtop stored locally; a
// 401 for this locked webtop opens the unlock dialog),
// modal() (backdrop click and data-close buttons for every <dialog>), toast() notifications and confirmDialog().
import { lineIcon } from '../widgets/kit.js';
import { humanProved, storage } from './applets.js';
import { showUnlock } from './lock.js';
import { slug } from './state.js';

const $toasts = document.getElementById('toasts');

// What takes typed text: a key pressed in one of these is the field's, never a shortcut's or type to search's.
export const TYPING = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

// A request about the open webtop whose answer comes after another webtop was opened in its place (client/lib/nav.js)
// throws a `stale` error instead of answering: whatever the caller meant to do next (draw the reply, add a tile,
// send the next step of a move) belongs to the webtop that is gone, so nothing after its `await` runs. toast()
// ignores it, so `.catch(toast)` and `catch (e) { toast(e); }` need nothing more. The server has still done the
// work: only this page stops following it. `err.data` is the answer, for the rare caller that reports it anyway.
// A request about a webtop stored in this browser (a `_` slug) never reaches the server: the storage applet
// (applets/storage, its service's request()) answers it from the browser's store, in the same shape, so callers need
// not know where the webtop they are on is stored.
const aboutWebtop = (path, s) => new RegExp(`^/api/webtops/${s}(?:[/?]|$)`).test(path);
const localPath = (path) => /^\/api\/webtops\/_/.test(path);
export async function api(method, path, body, raw) {
  const at = slug;
  const send = async () => (localPath(path) ? (await storage()).request(method, path, body, raw) : fetch(path, {
    method,
    headers: raw ? { 'content-type': raw } : body ? { 'content-type': 'application/json' } : {},
    body: raw ? body : body ? JSON.stringify(body) : undefined,
  }));
  let res = await send();
  if (await humanProved(res)) res = await send(); // a new webtop's first write: the check, once, then the write again
  const left = slug !== at && aboutWebtop(path, at);
  if (!res.ok || left) {
    let data = {};
    try { data = res.status === 204 ? {} : await res.json(); } catch {}
    const err = new Error(left ? 'That webtop is no longer open' : data.error || `${res.status}`);
    err.data = data; // e.g. {locked, slug} for any webtop, which client/lib/nav.js reads when it opens another
    err.stale = left;
    err.locked = !left && !res.ok && !!data.locked && data.slug === slug; // this webtop needs unlocking (cookie missing or expired)
    if (err.locked && !path.endsWith('/unlock')) showUnlock(data);
    throw err;
  }
  return res.status === 204 ? null : res.json();
}

// Shared behaviour for every <dialog>: a pointerdown on the backdrop closes it, and so does any button marked
// data-close. Escape already closes via the browser. The backdrop check uses the dialog's box rather than the
// event target, so a press on the dialog's own scrollbar does not count as outside.
export function modal($d, { backdrop = true } = {}) {
  $d.addEventListener('pointerdown', (e) => {
    if (!backdrop || e.target !== $d) return;
    const r = $d.getBoundingClientRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside) $d.close();
  });
  $d.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => $d.close()));
  return $d;
}

// Notifications. toast(message) shows a short card above the notch: an Error is shown as an error, a message
// ending in "…" as work in progress (spinner, stays until the next toast replaces it), anything else as a
// success; { kind: 'error' | 'info' | 'success' | 'busy' } overrides that. Up to TOAST_MAX stack, newest nearest
// the notch; the same message again refreshes the card instead of adding one. A click or tap dismisses a card,
// and hovering pauses its timer.
const TOAST_MAX = 3;
const TOAST_MS = { success: 2600, info: 4000, error: 5000, busy: 20000 };
export function toast(msg, { kind } = {}) {
  if (msg?.stale) return; // about a webtop that is no longer open (see api())
  const text = msg instanceof Error ? msg.message : String(msg);
  kind ??= msg instanceof Error ? 'error' : text.endsWith('…') ? 'busy' : 'success';
  $toasts.querySelectorAll('.toast.busy:not(.leaving)').forEach(dismissToast); // the work it announced is done
  let el = [...$toasts.querySelectorAll('.toast:not(.leaving)')].find((t) => t.dataset.text === text && t.dataset.kind === kind);
  if (!el) {
    el = document.createElement('div');
    el.className = `toast ${kind}`;
    el.dataset.text = text; el.dataset.kind = kind;
    el.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    el.innerHTML = `${lineIcon(kind, 'toast-icon')}<span class="toast-text"></span>`; // client/icons/<kind>.svg
    el.querySelector('.toast-text').textContent = text;
    el.addEventListener('click', () => dismissToast(el));
    el.addEventListener('pointerenter', () => clearTimeout(el.timer));
    el.addEventListener('pointerleave', () => armToast(el));
    $toasts.appendChild(el);
    const live = [...$toasts.querySelectorAll('.toast:not(.leaving)')];
    live.slice(0, Math.max(0, live.length - TOAST_MAX)).forEach(dismissToast);
  } else {
    el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump'); // a small pulse for a repeat
  }
  armToast(el);
}
function armToast(el) {
  clearTimeout(el.timer);
  el.timer = setTimeout(() => dismissToast(el), TOAST_MS[el.dataset.kind]);
}
function dismissToast(el) {
  clearTimeout(el.timer);
  if (el.classList.contains('leaving')) return;
  el.classList.add('leaving');
  const done = () => el.remove();
  el.addEventListener('animationend', done, { once: true });
  setTimeout(done, 400); // in case animations are off
}

// Reusable confirmation dialog. Resolves true when the confirm button is pressed, false on cancel/backdrop/Escape.
const $confirmDlg = modal(document.getElementById('confirm-dlg'));
/** Saves `data` (text, bytes or a Blob) as a file named `name` in the browser's downloads. */
export function download(data, type, name) {
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([data], { type })), download: name,
  });
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export function confirmDialog({ title, text, button = 'OK', danger = false, alt = null }) {
  return new Promise((resolve) => {
    document.getElementById('confirm-title').textContent = title;
    document.getElementById('confirm-text').textContent = text;
    const ok = document.getElementById('confirm-ok');
    ok.textContent = button;
    ok.classList.toggle('danger-fill', danger);
    // `alt` adds a second answer between Cancel and the main button; choosing it resolves 'alt'.
    const other = document.getElementById('confirm-alt');
    other.hidden = !alt;
    other.textContent = alt ?? '';
    let answer = false;
    const onOk = () => { answer = true; $confirmDlg.close(); };
    const onAlt = () => { answer = 'alt'; $confirmDlg.close(); };
    ok.addEventListener('click', onOk);
    other.addEventListener('click', onAlt);
    $confirmDlg.addEventListener('close', () => {
      ok.removeEventListener('click', onOk);
      other.removeEventListener('click', onAlt);
      resolve(answer);
    }, { once: true });
    $confirmDlg.showModal();
    ok.focus();
  });
}
